import { useQuery } from '@tanstack/react-query';

/**
 * Interface for the parsed documentation data
 */
export interface DocumentData {
  /** The raw markdown content */
  content: string;
  /** The document title extracted from the first heading or filename */
  title: string;
  /** The file path */
  path: string;
}

/**
 * Interface for the hook return value
 */
interface UseDocumentationReturn {
  /** The parsed document data */
  document: DocumentData | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether the document was not found */
  isNotFound: boolean;
  /** Function to refetch the document */
  refetch: () => void;
}

/**
 * Extracts the title from markdown content
 * Looks for the first H1 heading, otherwise uses the filename
 *
 * @param content - The markdown content
 * @param path - The file path
 * @returns The extracted title
 */
function extractTitle(content: string, path: string): string {
  // Try to find first H1 heading
  const h1Match = content.match(/^#\s+(.+)$/m);
  if (h1Match) {
    return h1Match[1].trim();
  }

  // Fallback to filename without extension
  const filename = path.split('/').pop() || '';
  return filename.replace(/\.md$/, '').replace(/-/g, ' ');
}

/**
 * Custom hook for fetching and parsing markdown documentation files
 *
 * @description
 * This hook fetches markdown files from the `/docs/` directory and parses them into
 * a structured format. It handles loading states, errors, and 404 responses.
 *
 * Features:
 * - Automatic title extraction from markdown content or filename
 * - Built-in caching via React Query
 * - Error handling with 404 detection
 * - TypeScript type safety
 *
 * @param path - The relative path to the markdown file (e.g., "getting-started/introduction.md")
 *
 * @returns An object containing the document data, loading state, and error state
 *
 * @example
 * ```tsx
 * function DocumentationPage() {
 *   const { document, isLoading, error, isNotFound } = useDocumentation('getting-started/introduction.md');
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (isNotFound) return <div>Document not found</div>;
 *   if (error) return <div>Error loading document</div>;
 *
 *   return <MarkdownRenderer content={document.content} />;
 * }
 * ```
 */
export function useDocumentation(path: string | undefined | null): UseDocumentationReturn {
  const {
    data: document,
    isLoading,
    error,
    refetch,
  } = useQuery<DocumentData, Error>({
    queryKey: ['documentation', path],
    queryFn: async () => {
      if (!path) {
        throw new Error('No path provided');
      }

      // Fetch the markdown file from the public docs directory
      // Using native fetch instead of axios to avoid interceptors
      const response = await fetch(`/docs/${path}`, {
        headers: {
          'Content-Type': 'text/plain',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
      }

      const content = await response.text();
      const title = extractTitle(content, path);

      return {
        content,
        title,
        path,
      };
    },
    enabled: !!path,
    retry: false, // Don't retry on 404
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Check if error is 404
  const isNotFound = error ? error.message?.includes('404') : false;

  return {
    document,
    isLoading,
    error,
    isNotFound,
    refetch,
  };
}
